import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  OnDestroy,
  resource,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Bank,
  BookingService,
  BookingServiceService,
  ClassificationCodeService,
  CodeType,
  CustomerSearch,
  CustomerService,
  EmployeeProcedureService,
  EmployeeService,
  User,
  VisitService,
} from '@beautyapp/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  firstValueFrom,
  forkJoin,
  Observable,
  of,
  Subject,
  throwError,
} from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { format } from 'date-fns';
import { AddUserComponent } from '../components/add-user/add-user.component';
import { UserViewComponent } from '@beautyapp/users';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import {
  createDateRangeValidator,
  validateSum,
} from '../validators/appointment.validator';
import { UserVerifyComponent } from '../components';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class VisitAddFacade implements OnDestroy {
  private translateService: TranslateService = inject(TranslateService);
  private customerService: CustomerService = inject(CustomerService);
  private employeeService: EmployeeService = inject(EmployeeService);
  private modalService: NzModalService = inject(NzModalService);
  private message: NzMessageService = inject(NzMessageService);
  private drawerService: NzDrawerService = inject(NzDrawerService);
  private employeeServiceService: EmployeeProcedureService = inject(
    EmployeeProcedureService
  );
  bookingService: BookingService = inject(BookingService);
  bookingServiceService: BookingServiceService = inject(BookingServiceService);
  visitService: VisitService = inject(VisitService);
  classificationCodeService = inject(ClassificationCodeService);

  sub$: Subject<any> = new Subject<any>();

  form: FormGroup = new FormGroup(
    {
      id: new FormControl(null),
      isAnonymous: new FormControl(false),
      anonymousName: new FormControl(null),
      withOutService: new FormControl(false),
      cardNo: new FormControl(null),
      employeeId: new FormControl(null, Validators.required),
      onUserRequest: new FormControl(false),
      salaryFixed: new FormControl(null),
      // beaut-424: a share of a price cannot exceed the price. The backend
      // rejects >100 with a 422; without this the operator only discovers that
      // at final save, as a status code instead of a message under the field.
      salaryPercent: new FormControl(null, [
        Validators.min(0),
        Validators.max(100),
      ]),
      salaryPercentFromPrice: new FormControl(false),
      userId: new FormControl(null, Validators.required),
      status: new FormControl(1, Validators.required),
      paymentType: new FormControl(null),
      price: new FormControl(0),
      salePrice: new FormControl(null),
      salePercent: new FormControl(null),
      cash: new FormControl(0),
      card: new FormControl(0),
      minutes: new FormControl(0),
      balance: new FormControl(0),
      barter: new FormControl(0),
      consignation: new FormControl(0),
      bankTransfer: new FormControl(0),
      fixedPrice: new FormControl(null),
      bookingDate: new FormControl(null, Validators.required),
      startTime: new FormControl(null, Validators.required),
      endTime: new FormControl(null, Validators.required),
      isGift: new FormControl(false),
      sendNotification: new FormControl(true),
      isConsignation: new FormControl(false),
      description: new FormControl(null),
      insuranceId: new FormControl(null),
      insurancePercent: new FormControl(null),
      insurancePolicy: new FormControl(null),
      serviceId: new FormControl(null),
    },
    {
      validators: [createDateRangeValidator(), validateSum()],
    }
  );

  paymentForm = new FormGroup(
    {
      visitId: new FormControl(null, Validators.required),
      paymentType: new FormControl<number | null>(null, Validators.required),
      price: new FormControl(0, Validators.required),
      salePrice: new FormControl(null),
      cash: new FormControl(0),
      card: new FormControl(0),
      minutes: new FormControl(0),
      balance: new FormControl(0),
      barter: new FormControl(0),
      consignation: new FormControl(0),
      bankTransfer: new FormControl(0),
      bank: new FormControl<Bank | null>(null),
      bankTransferBank: new FormControl<Bank | null>(null),
    },
    {
      validators: [validateSum()],
    }
  );

  // Rebuilt reactively from onLangChange / onTranslationChange so the dropdown
  // never shows raw keys (PAYMENTTYPE.1) when the translation bundle hasn't
  // loaded yet at facade-construction time after a hard reload.
  // translateService.instant() falls back to the key when the bundle isn't
  // ready, and this list used to be frozen at construction time.
  paymentTypes: { id: number; name: string }[] = this.buildPaymentTypes();

  private readonly _paymentTypesSync = this.translateService.onLangChange
    .pipe(untilDestroyed(this))
    .subscribe(() => (this.paymentTypes = this.buildPaymentTypes()));
  private readonly _paymentTypesTranslationSync =
    this.translateService.onTranslationChange
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.paymentTypes = this.buildPaymentTypes()));
  private readonly _paymentTypesDefaultLangSync =
    this.translateService.onDefaultLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.paymentTypes = this.buildPaymentTypes()));

  private buildPaymentTypes(): { id: number; name: string }[] {
    const t = (key: string) => this.translateService.instant(key);
    return [
      { id: 1, name: t('PAYMENTTYPE.1') },
      { id: 2, name: t('PAYMENTTYPE.2') },
      { id: 9, name: t('PAYMENTTYPE.9') },
      { id: 5, name: t('PAYMENTTYPE.5') },
      { id: 6, name: t('PAYMENTTYPE.6') },
      { id: 7, name: t('PAYMENTTYPE.7') },
      { id: 8, name: t('PAYMENTTYPE.8') },
      { id: 4, name: t('PAYMENTTYPE.4') },
      { id: 11, name: t('PAYMENTTYPE.11') },
    ];
  }

  // Payment types that allow completing a visit with zero money exchanged.
  // Used when the visit total is 0 and only these options should appear.
  private readonly zeroTotalPaymentTypeIds = new Set<number>([6, 11]);

  get zeroTotalPaymentTypes(): any[] {
    return this.paymentTypes.filter((pt: any) =>
      this.zeroTotalPaymentTypeIds.has(pt.id)
    );
  }

  // Payment types shown when the visit total is > 0.
  // Excludes Free (11) which is only valid when nothing is owed.
  get regularPaymentTypes(): any[] {
    return this.paymentTypes.filter((pt: any) => pt.id !== 11);
  }

  banks: {
    id: Bank;
    name: string;
  }[] = [
    { id: Bank.TBC, name: 'TBC bank' },
    { id: Bank.BOG, name: 'Bank of Georgia' },
    { id: Bank.Liberty, name: 'Liberty' },
    { id: Bank.Other, name: 'Other' },
  ];

  employee$: Observable<any> = this.employeeService.getDropdown().pipe(
    switchMap((res) => {
      return of(
        res.map((m: any) => ({
          ...m,
          fullName: m.user?.fullName || '',
        }))
      );
    })
  );

  currentEmployee = new BehaviorSubject<any>({} as any);
  selectedEmployee$: Observable<any> = this.currentEmployee.asObservable();
  prevEmployee: any;
  onlineBookingId?: string;
  loading = signal(false);

  get getCurrentEmployee() {
    return this.currentEmployee.getValue();
  }

  employeeServices = new BehaviorSubject<any[]>([]);
  employeeServices$: Observable<any[]> = this.employeeServices.asObservable();

  services = new BehaviorSubject<any[]>([]);
  services$: Observable<any[]> = this.services.asObservable();

  bookings = signal<any[]>([]);
  realizations = signal<any[]>([]);
  ehrs = signal<any[]>([]);
  employee = signal<any>(null);
  visit = signal<any>(null);

  // Visit payment signals
  visitPayments = signal<any[]>([]);
  totalPaid = computed(() =>
    this.visitPayments().reduce((sum, p) => {
      if (+p.paymentType === 7) {
        // For consignation drafts, only count actual monetary payments (not the consignation debt)
        return (
          sum +
          (+p.cash || 0) +
          (+p.card || 0) +
          (+p.bank || 0) +
          (+p.balance || 0)
        );
      }
      return sum + (+p.price || 0);
    }, 0)
  );
  totalConsignation = computed(() =>
    this.visitPayments().reduce((sum, p) => {
      if (+p.paymentType === 7) {
        // Consignation amount = total price minus actual payments stored in the draft
        return (
          sum +
          ((+p.price || 0) -
            (+p.cash || 0) -
            (+p.card || 0) -
            (+p.bank || 0) -
            (+p.balance || 0))
        );
      }
      return sum;
    }, 0)
  );
  remainingAmount = computed(
    () => this.totalPayPrice() - this.totalPaid() - this.totalConsignation()
  );
  totalMinutesPaid = computed(() =>
    this.visitPayments().reduce((sum, p) => sum + (+p.minutes || 0), 0)
  );
  remainingMinutes = computed(
    () => this.totalMinutes() - this.totalMinutesPaid()
  );
  isFullyPaid = computed(() => {
    // If any saved payment is barter (paymentType 6), consider fully paid
    const hasBarterPayment = this.visitPayments().some(
      (p) => +p.paymentType === 6
    );
    if (hasBarterPayment) return true;

    const hasMoney = this.totalPayPrice() > 0;
    const hasMinutes = this.totalMinutes() > 0;
    const moneyPaid = this.remainingAmount() <= 0;
    const minutesPaid = this.remainingMinutes() <= 0;

    if (!hasMoney && !hasMinutes) return true;
    if (hasMoney && hasMinutes) return moneyPaid && minutesPaid;
    if (hasMoney) return moneyPaid;
    return minutesPaid;
  });

  // True only when nothing is owed AND no prior payments have been recorded.
  // The visitPayments guard prevents re-opening the restricted Barter/Free dropdown
  // after payments have already been captured (e.g., by a different operator).
  isZeroTotal = computed(() => {
    return (
      this.totalPayPrice() === 0 &&
      this.totalMinutes() === 0 &&
      this.visitPayments().length === 0
    );
  });

  bookingCount = computed(() => this.bookings()?.length || 0);

  get getServices() {
    return this.services.getValue();
  }

  specServices = new BehaviorSubject<any[]>([]);
  specServices$: Observable<any[]> = this.specServices.asObservable();

  get getSpecServices() {
    return this.specServices.getValue();
  }

  isLoading = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoading.asObservable();

  // Shared customer-search state (search + stable customers$ + reset). Own
  // instance per facade — see CustomerSearch (a shared root singleton would bleed
  // the booking flow's list into the visit flow).
  private readonly customerSearch = new CustomerSearch(this.customerService);
  customers$: Observable<User[]> = this.customerSearch.customers$;

  _modalRef!: NzModalRef;

  get modalRef() {
    return this._modalRef;
  }

  set modalRef(modalRef: NzModalRef) {
    this._modalRef = modalRef;
  }

  get status() {
    return this.form.get('status')?.value;
  }

  get isCompleted() {
    return this.status === 'COMPLETED';
  }

  get startTime() {
    const startTime = this.form.get('startTime')?.value;
    return format(new Date(startTime), 'yyyy-MM-dd HH:mm');
  }

  get nextStartTime() {
    const servicesGreterEndTime =
      this.bookings()?.filter((b) => {
        return new Date(b.endTime) > new Date(this.endTime);
      }) || [];

    if (servicesGreterEndTime.length) {
      return format(
        new Date(servicesGreterEndTime[0].startTime),
        'yyyy-MM-dd HH:mm'
      );
    } else {
      return this.endTime;
    }
  }

  get bookingDate() {
    const bookingDate = this.form.get('bookingDate')?.value;
    return format(new Date(bookingDate), 'yyyy-MM-dd');
  }

  get endTime() {
    const endTime = this.form.get('endTime')?.value;
    return format(new Date(endTime), 'yyyy-MM-dd HH:mm');
  }

  get bookingId() {
    return this.form.get('id')?.value;
  }

  set bookingId(id: string) {
    this.form.get('id')?.setValue(id);
  }

  get visitId() {
    return this.form.get('id')?.value;
  }

  set visitId(id: string) {
    this.form.get('id')?.setValue(id);
  }

  _booking: any;
  get booking(): any {
    return this._booking;
  }

  set booking(booking: any) {
    this._booking = booking;
  }

  get userId() {
    return this.form.get('userId')?.value;
  }

  set userId(userId: string) {
    this.form.get('userId')?.setValue(userId);
  }

  get employeeId() {
    return this.form.get('employeeId')?.value;
  }

  set employeeId(employeeId: string) {
    this.form.get('employeeId')?.setValue(employeeId);
  }

  user = signal<User>({} as User);

  get invalid() {
    return this.form.invalid;
  }

  get paymentType() {
    return this.paymentForm.get('paymentType')?.value;
  }

  visitEmployees = computed(() => {
    return this.bookings()?.map((m) => {
      return {
        id: m.employeeId,
        name: m.employee?.user?.fullName || '',
      };
    });
  });

  uniqueEmployees = computed(() => {
    const employees = this.bookings().map((booking) => ({
      id: booking.employee.id,
      name: booking.employee?.user?.fullName || '',
    }));

    // Using Map to keep only unique employees by ID
    const uniqueEmployeesMap = new Map();
    employees.forEach((employee) => {
      if (!uniqueEmployeesMap.has(employee.id)) {
        uniqueEmployeesMap.set(employee.id, employee);
      }
    });

    return Array.from(uniqueEmployeesMap.values());
  });

  totalPrice = computed(() => {
    return this.bookings()?.reduce((acc, b) => {
      return b.totalPrice + acc;
    }, 0);
  });

  totalExpense = computed(() => {
    return this.bookings()?.reduce((acc, b) => {
      return acc + +b.totalExpense;
    }, 0);
  });

  totalMinutes = computed(() => {
    console.log('totalMinutes', this.bookings());
    return this.bookings()?.reduce((acc, b) => {
      return acc + +b.totalMinutes;
    }, 0);
  });

  totalRealizationPrice = computed(() => {
    return this.realizations().reduce((acc, b) => {
      console.log(b);
      if (b.discountPrice && b.discountPrice > 0) {
        return acc + +b.discountPrice;
      }
      return acc + +b.price;
    }, 0);
  });

  totalPayPrice = computed(() => {
    return this.totalPrice() + this.totalRealizationPrice();
  });

  totalMinutesPrice = computed(() => {
    return this.bookings()
      ?.filter((m) => m.bookingService?.service?.serviceType === 'MINUTE')
      .reduce((acc, b) => {
        return acc + +b.bookingService?.lastPrice;
      }, 0);
  });

  checkDisableStatus = (tabName: string, tabIndex: number) => {
    switch (tabName) {
      case 'services':
        return this.bookings()?.length === 0;
      case 'realization':
        return !this.visitId;
      case 'ehr':
        return !this.visitId;
      case 'payment':
        return this.bookings()?.length === 0 || !this.visitId;
      default:
        return false;
    }
  };

  verify(user?: User) {
    if (user && user.isVerified) {
      return;
    }

    this.modalService.create({
      nzTitle: this.translateService.instant('users.verifyUser'),
      nzContent: UserVerifyComponent,
      nzWidth: '400px',
      nzData: {
        userId: user?.id,
      },
      nzOnOk: () => {
        this.getVisitById(+this.visitId)
          .pipe(
            tap((visit) => {
              this.visit.set(visit);
              this.user.set(visit.user);
            }),
            untilDestroyed(this)
          )
          .subscribe();
      },
    });
  }

  addNewUser() {
    const modal = this.modalService.create({
      nzTitle: this.translateService.instant('calendar.createUser'),
      nzContent: AddUserComponent,
      nzWidth: '900px',
      nzStyle: { top: '0px' },
      nzData: {
        userId: this.userId,
      },
    });

    modal.afterClose.pipe(takeUntil(this.sub$)).subscribe((res) => {
      if (res) {
        this.user.set(res);
        this.userId = res.id;
        this.form.patchValue({
          userId: res.id,
        });
      }
    });
  }

  viewUser(user?: User) {
    if (!user) {
      user = this.user();
    }

    let title = `${user.firstname} ${user.lastname || ''}`;
    if (user.cardNo) {
      title += ` - # ${user.cardNo}`;
    }
    this.drawerService
      .create<UserViewComponent, { data: string }, string>({
        nzTitle: title,
        nzContent: UserViewComponent,
        nzWrapClassName: 'user-drawer',
        nzBodyStyle: {
          padding: '0 15px 15px 15px',
        },
        nzData: {
          data: user.id,
        },
      })
      .afterClose.pipe(
        untilDestroyed(this),
        switchMap(() => this.customerService.getById(user.id))
      )
      .subscribe((res) => {
        console.log(res);
        this.user.set(res);
      });
  }

  searchCustomer(search: any) {
    // beaut-366 fix now lives in the shared CustomerSearch helper (empty term
    // loads the first page; results push into a stable customers$; reset() clears
    // it between visits). See CustomerSearch.
    this.customerSearch.search(search);
  }

  selectCustomer($event: any) {
    console.log($event);
    this.userId = $event.id;
    this.user.set($event);
  }

  ngOnDestroy() {
    this.sub$.next(null);
    this.sub$.complete();
  }

  addVisit() {
    console.log(this.form);

    this.form.markAllAsTouched();

    if (this.form.invalid) return throwError(() => 'Invalid form');

    const {
      id,
      employeeId,
      isAnonymous,
      anonymousName,
      userId,
      onUserRequest,
      bookingDate,
      startTime,
      endTime,
      description,
      sendNotification,
      withOutService,
      serviceId,
      price,
      salePrice,
      salePercent,
      salaryFixed,
      salaryPercent,
      salaryPercentFromPrice,
      specServices,
      se,
    } = this.form.value;
    console.log('form value', this.form.value);
    console.log('visitId', this.visitId);
    if (this.visitId) {
      return this.bookingService.updateVisit({
        id: +this.visitId,
        bookingDate,
        description,
        sendNotification,
      });
    } else {
      return this.bookingService.createVisit({
        id,
        employeeId,
        isAnonymous,
        anonymousName,
        userId,
        onUserRequest,
        bookingDate,
        startTime,
        endTime,
        description,
        sendNotification,
        withOutService,
        serviceId,
        price,
        salePrice,
        salePercent: +salePercent,
        salaryFixed: +salaryFixed,
        salaryPercent: +salaryPercent,
        salaryPercentFromPrice,
      });
    }
  }

  getVisitById(id: number) {
    return this.bookingService.getVisitById(id);
  }

  createVisitBooking(booking: any) {
    return this.bookingService.createVisitBooking(booking);
  }

  deleteBooking(id: string) {
    return this.bookingService.delete(id);
  }

  updateVisitBooking(param: {
    id: any;
    visitId: any;
    employeeId: null | undefined;
    startTime: any;
    endTime: any;
  }) {
    return this.bookingService.updateVisitBooking(param);
  }

  loadEmployee(employeeId: any) {
    return this.employeeService.getById(employeeId);
  }

  reset() {
    this.user.set({} as User);
    this.visit.set(null);
    // beaut-366: clear the customer dropdown so the next visit starts fresh.
    this.customerSearch.reset();
    this.bookings.set([]);
    this.ehrs.set([]);
    this.realizations.set([]);
    this.employee.set(null);
    this.visitPayments.set([]);
    this.form.patchValue({
      id: null,
      isAnonymous: false,
      anonymousName: null,
      withOutService: false,
      cardNo: null,
      employeeId: null,
      onUserRequest: false,
      salaryFixed: null,
      salaryPercent: null,
      userId: null,
      status: 1,
      paymentType: null,
      price: 0,
      salePrice: null,
      cash: 0,
      card: 0,
      minutes: 0,
      balance: 0,
      barter: 0,
      consignation: 0,
      bankTransfer: 0,
      fixedPrice: null,
      bookingDate: null,
      startTime: null,
      endTime: null,
      isGift: false,
      sendNotification: true,
      isConsignation: false,
      description: null,
      insuranceId: null,
      insurancePercent: null,
      insurancePolicy: null,
      serviceId: null,
    });
    this.paymentForm.patchValue({
      visitId: null,
      paymentType: null,
      price: 0,
      salePrice: null,
      cash: 0,
      card: 0,
      minutes: 0,
      balance: 0,
      barter: 0,
      consignation: 0,
      bankTransfer: 0,
      bank: null,
    });
  }

  addPayment() {
    this.paymentForm.markAllAsTouched();

    if (this.paymentForm.invalid)
      return throwError(() => new Error('Invalid form'));

    const {
      visitId,
      paymentType,
      price,
      salePrice,
      cash,
      card,
      minutes,
      balance,
      barter,
      consignation,
      bankTransfer,
      bankTransferBank,
      bank,
    } = this.paymentForm.value;

    return this.bookingService.createPayment({
      visitId,
      paymentType,
      price,
      salePrice,
      cash,
      card,
      minutes,
      balance,
      barter,
      consignation: Number(consignation),
      bankTransfer,
      bankTransferBank,
      bank,
    });
  }

  // ==================== Visit Payment Methods ====================

  loadPayments(visitId: number) {
    return this.bookingService.getVisitPayments(visitId).pipe(
      tap((payments) => {
        this.visitPayments.set(payments || []);
      })
    );
  }

  saveVisitPayment() {
    this.paymentForm.markAllAsTouched();
    if (this.paymentForm.invalid)
      return throwError(() => new Error('Invalid form'));

    const {
      paymentType,
      price,
      cash,
      card,
      minutes,
      balance,
      barter,
      consignation,
      bankTransfer,
      bank,
      bankTransferBank,
    } = this.paymentForm.value;
    const visitId = +this.visitId;

    return this.bookingService
      .saveVisitPayment(visitId, {
        paymentType,
        price,
        cash,
        card,
        minutes,
        balance,
        barter,
        consignation: Number(consignation),
        bankTransfer,
        bank,
        bankTransferBank,
      })
      .pipe(
        switchMap(() => this.loadPayments(visitId)),
        tap(() => {
          // Reset payment form after successful save
          this.paymentForm.patchValue({
            paymentType: null,
            price: 0,
            salePrice: null,
            cash: 0,
            card: 0,
            minutes: 0,
            balance: 0,
            barter: 0,
            consignation: 0,
            bankTransfer: 0,
            bank: null,
            bankTransferBank: null,
          });
        })
      );
  }

  saveAndCompleteVisit(confirmExpiredPackage = false) {
    this.paymentForm.markAllAsTouched();
    if (this.paymentForm.invalid)
      return throwError(() => new Error('Invalid form'));

    const {
      paymentType,
      price,
      cash,
      card,
      minutes,
      balance,
      barter,
      consignation,
      bankTransfer,
      bank,
      bankTransferBank,
    } = this.paymentForm.value;
    const visitId = +this.visitId;

    return this.bookingService.completeVisitWithSavedPayments({
      visitId,
      paymentType,
      price,
      cash,
      card,
      minutes,
      balance,
      barter,
      consignation: Number(consignation),
      bankTransfer,
      bank,
      bankTransferBank,
      confirmExpiredPackage,
    });
  }

  completeVisit(confirmExpiredPackage = false) {
    const visitId = +this.visitId;
    return this.bookingService.completeVisitWithSavedPayments({
      visitId,
      paymentType: null,
      price: 0,
      cash: 0,
      card: 0,
      minutes: 0,
      balance: 0,
      barter: 0,
      consignation: 0,
      bankTransfer: 0,
      bank: null,
      bankTransferBank: null,
      confirmExpiredPackage,
    });
  }

  deleteVisitPayment(visitId: number, paymentId: number) {
    return this.bookingService
      .deleteVisitPayment(visitId, paymentId)
      .pipe(switchMap(() => this.loadPayments(visitId)));
  }

  printReceipt() {
    return this.bookingService.downloadInvoice(this.visitId);
  }

  deleteRealization(id: number) {
    return this.visitService.deleteVisitRealization(id);
  }

  getEmployeeProcedures(employeeId: unknown) {
    console.log('bookings', this.bookings());
    return this.bookings()
      .filter((b) => b.employee.id === employeeId)
      .map((b) => {
        return b.bookingServices
          ?.map((bs: any) => {
            if (!bs.service) {
              return null;
            }
            return {
              id: bs.service.id,
              name: bs.service.name,
              price: bs.price,
              minutes: bs.minutes,
              serviceType: bs.service.serviceType,
              employeeId: b.employee.id,
              employeeName: b.employee?.user?.fullName || '',
              bookingDate: b.bookingDate,
              startTime: b.startTime,
              endTime: b.endTime,
            };
          })
          .filter((bss: any) => bss !== null);
      })
      .flat();
  }

  getBookings(visitId: number) {
    return this.visitService.getBookings(visitId).pipe(
      tap((bookings) => {
        console.log('bookings', bookings);
        this.bookings.update(() => bookings);
      })
    );
  }

  getRealizations(visitId: number) {
    return this.visitService.getVisitRealizations(visitId).pipe(
      tap((realizations) => {
        console.log('realizations', realizations);
        this.realizations.update(() => realizations);
      })
    );
  }

  updateUser(user: User) {
    return of(user);
    // return this.customerService.updateUser(user.id!, user).pipe(
    //   tap((updatedUser) => {
    //     this.user.set(updatedUser);
    //   }),
    //   catchError((error) => {
    //     console.error('Error updating user:', error);
    //     return throwError(() => error);
    //   })
    // );
  }

  updateUserRequest($event: boolean) {
    this.loading.set(true);
    if (!this.visit()?.id) {
      this.loading.set(false);
      return;
    }

    return this.visitService
      .updateNotificationSettings(this.visit().id, {
        onUserRequest: $event,
        sendNotification: this.visit()?.sendNotification,
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
          this.visit.update((visit) => ({
            ...visit,
            onUserRequest: $event,
          }));
        }),
        catchError((error) => {
          this.loading.set(false);
          return throwError(() => error);
        })
      );
  }

  updateSmsNotification(sendNotification: boolean, onUserRequest: boolean) {
    return this.visitService
      .updateNotificationSettings(this.visit().id, {
        sendNotification,
        onUserRequest,
      })
      .pipe(
        tap(() => {
          this.visit.update((visit) => ({
            ...visit,
            sendNotification,
            onUserRequest,
          }));
        }),
        catchError((error) => {
          this.loading.set(false);
          return throwError(() => error);
        })
      );
  }

  updateVisitDate(newDate: Date | null) {
    return this.visitService.updateDate(this.visit().id, { newDate });
  }

  sendRecipe(params: {
    visitId: string;
    email: string | null | undefined;
    sendToMobile: boolean | null | undefined;
  }) {
    return this.visitService.sendRecipe(params);
  }

  saveUser(selectedUser: User | undefined) {
    const visitId = this.visitId;
    return this.visitService
      .updateVisitUser(visitId, selectedUser?.id)
      .pipe(
        switchMap((res) =>
          this.clearOldCustomerPackageBookings(+visitId).pipe(map(() => res))
        )
      );
  }

  /**
   * beaut-377: when the visit's customer is changed, any booking service that
   * was attached from the PREVIOUS customer's package must not carry over —
   * otherwise the new customer would consume someone else's paid package.
   *
   * These services (marked by `packagePurchaseId`) only exist because of the
   * old customer's package entitlement and carry package-specific pricing that
   * is meaningless for the new customer, so we REMOVE them outright rather than
   * strip the id and leave an ambiguously-priced service behind. Normal
   * (non-package) services are left untouched. Re-fetches the visit's bookings
   * from the backend so the decision is made on the persisted truth, not a
   * possibly-stale UI signal. The customer's own available packages are
   * re-fetched separately by the choose-service dropdown, which is keyed on the
   * (now updated) userId.
   */
  private clearOldCustomerPackageBookings(
    visitId: number
  ): Observable<unknown> {
    if (!visitId) {
      return of(null);
    }
    return this.visitService.getBookings(visitId).pipe(
      switchMap((bookings: any[]) => {
        const packageServiceIds = (bookings || [])
          .flatMap((b) => b?.bookingServices || [])
          .filter((bs: any) => bs?.packagePurchaseId && bs?.id)
          .map((bs: any) => bs.id);
        if (packageServiceIds.length === 0) {
          return of(null);
        }
        const deletes = packageServiceIds.map((id) =>
          this.bookingServiceService.delete(id).pipe(
            map(() => true),
            catchError((err) => {
              // Data-integrity fix: a swallowed failure leaves the old
              // customer's package-covered booking attached (the exact leak)
              // while the UI reports success — so make it visible instead.
              console.error(
                '[beaut-377] failed to clear package booking service',
                id,
                err
              );
              return of(false);
            })
          )
        );
        return forkJoin(deletes).pipe(
          tap((results) => {
            if (results.some((ok) => !ok)) {
              this.message.warning(
                this.translateService.instant('calendar.packageClearFailed')
              );
            }
          }),
          map(() => null)
        );
      }),
      catchError((err) => {
        console.error(
          '[beaut-377] clearOldCustomerPackageBookings failed',
          err
        );
        return of(null);
      })
    );
  }
}
